import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { useMessageStore } from '../../store/messageStore';
import { ThemeToggle } from '../common/ThemeToggle';
import { MessageIcon } from '../common/MessageIcon';
import { StatusSelector } from '../common/StatusSelector';
import { userService } from '../../api/services/userService';
import { UserStatus } from '@shared/types';
import { toast } from 'react-hot-toast';
import {
  LayoutDashboard,
  ShoppingCart,
  Factory,
  Shield,
  Zap,
  Scissors,
  UserCheck,
  Package,
  Truck,
  Boxes,
  BookOpen,
  Wrench,
  FileText,
  ClipboardCheck,
  Receipt,
  Users,
  Upload,
  Car,
  UserCog,
  BarChart3,
  DollarSign,
  Route,
  FileEdit,
  BarChart,
  Ruler,
  SearchCheck,
  Database,
  PieChart,
  ChevronDown,
  ChevronRight,
  LogOut,
  Menu,
  X,
  UserPlus,
  Clock,
  BookMarked,
  ShoppingBag,
} from 'lucide-react';

interface MainLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  label: string;
  to?: string;
  icon: React.ReactNode;
  children?: NavItem[];
  // If defined, only these roles see the item (Administrator always bypasses). Empty array = admin only.
  allowedRoles?: string[];
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, logout } = useAuthStore();
  const { startPolling, stopPolling } = useMessageStore();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Refs for idle/auto-away logic
  const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
  const isAutoAway = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentStatusRef = useRef<UserStatus>('available');
  const startIdleTimerRef = useRef<() => void>(() => {});

  // Fetch current user status
  const { data: currentStatus = 'available' as UserStatus } = useQuery({
    queryKey: ['myStatus'],
    queryFn: userService.getMyStatus,
    staleTime: 30000,
  });

  // Keep status ref in sync for use in event handlers
  useEffect(() => {
    currentStatusRef.current = currentStatus;
  }, [currentStatus]);

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: userService.updateMyStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myStatus'] });
      queryClient.invalidateQueries({ queryKey: ['activeUsers'] });
    },
    onError: () => {
      toast.error('Failed to update status');
    },
  });

  // Set available on login (mount)
  useEffect(() => {
    updateStatusMutation.mutate('available');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set offline when tab/window is closed
  useEffect(() => {
    const handleBeforeUnload = () => {
      const token = localStorage.getItem('token');
      if (token) {
        fetch('/api/v1/users/me/status', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ status: 'offline' }),
          keepalive: true,
        });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Idle detection: auto-away after 5 min, restore to available on activity
  useEffect(() => {
    const startIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (currentStatusRef.current === 'available') {
          isAutoAway.current = true;
          updateStatusMutation.mutate('away');
        }
      }, IDLE_TIMEOUT);
    };

    startIdleTimerRef.current = startIdleTimer;

    const handleActivity = () => {
      // Restore to available if we auto-set to away
      if (isAutoAway.current) {
        isAutoAway.current = false;
        updateStatusMutation.mutate('available');
      }
      // Only keep idle timer running when available
      if (currentStatusRef.current === 'available') {
        startIdleTimer();
      }
    };

    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    startIdleTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual status change — clears auto-away flag, restarts idle timer if switching to available
  const handleStatusChange = (status: UserStatus) => {
    isAutoAway.current = false;
    if (status === 'available') {
      startIdleTimerRef.current();
    }
    updateStatusMutation.mutate(status);
  };

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const isAdmin = user?.roles?.includes('Administrator') || false;
  const userRoles = user?.roles || [];

  const canSeeItem = (item: NavItem): boolean => {
    if (isAdmin) return true;
    if (item.allowedRoles === undefined) return true;
    if (item.allowedRoles.length === 0) return false;
    return item.allowedRoles.some(role => userRoles.includes(role));
  };

  const handleLogout = async () => {
    try {
      await userService.updateMyStatus('offline');
    } catch {}
    logout();
    navigate('/login');
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path: string) => location.pathname === path;
  const isSectionActive = (items: NavItem[]): boolean => {
    return items.some(item => {
      if (item.to && location.pathname === item.to) return true;
      if (item.children) return isSectionActive(item.children);
      return false;
    });
  };

  // Build navigation structure
  const navSections: NavItem[] = [
    {
      label: 'Orders Dashboard',
      to: '/production/orders-dashboard',
      icon: <LayoutDashboard size={20} />,
      allowedRoles: ['Orders', 'Returns', 'CustomerService'],
    },
    {
      label: 'Orders',
      icon: <ShoppingCart size={20} />,
      allowedRoles: ['Orders', 'Returns', 'CustomerService'],
      children: [
        { label: 'Pending Orders', to: '/orders/pending', icon: <Clock size={16} /> },
        { label: 'New Order', to: '/invoice/new', icon: <FileText size={16} /> },
        { label: 'Order Checking', to: '/orders/check', icon: <ClipboardCheck size={16} /> },
        { label: 'Order Import', to: '/orders/import', icon: <Upload size={16} /> },
        { label: 'NetSuite Invoicing', to: '/orders/netsuite-invoicing', icon: <Receipt size={16} /> },
      ],
    },
    {
      label: 'Production',
      icon: <Factory size={20} />,
      children: [
        { label: 'Cutting Orders', to: '/production/cutting-orders', icon: <Scissors size={16} />, allowedRoles: ['Orders', 'CustomerService'] },
        { label: 'Sewer Pick Up', to: '/production/sewer-pickup', icon: <UserCheck size={16} />, allowedRoles: ['Orders', 'CustomerService', 'Sewer'] },
        { label: 'Packaging', to: '/production/packaging', icon: <Package size={16} />, allowedRoles: ['Orders', 'CustomerService'] },
        { label: 'Shipping', to: '/shipping', icon: <Truck size={16} />, allowedRoles: ['Orders', 'CustomerService'] },
        { label: 'Stock', to: '/stock', icon: <Boxes size={16} />, allowedRoles: ['Orders', 'CustomerService'] },
        { label: 'Sewer Instructions', to: '/production/sewer-instructions', icon: <BookOpen size={16} />, allowedRoles: ['Orders', 'CustomerService', 'Sewer'] },
        { label: 'Open Repairs', to: '/production/open-repairs', icon: <Wrench size={16} />, allowedRoles: ['Orders', 'CustomerService', 'Returns'] },
      ],
    },
    {
      label: 'Pattern Operations',
      icon: <Ruler size={20} />,
      allowedRoles: [],
      children: [
        { label: 'New Pattern Form', to: '/admin/new-pattern-form', icon: <FileEdit size={16} /> },
        { label: 'Sewing Instruction Upload', to: '/admin/sewing-instruction-upload', icon: <Upload size={16} /> },
        { label: 'Installation Instructions', to: '/admin/installation-instructions', icon: <BookMarked size={16} /> },
      ],
    },
    {
      label: 'App Finder',
      to: '/vehicle-applications/app-finder',
      icon: <SearchCheck size={20} />,
      allowedRoles: ['Orders'],
    },
    {
      label: 'Marketplace Messages',
      to: '/marketplace/messages',
      icon: <ShoppingBag size={20} />,
      allowedRoles: ['Orders', 'Returns', 'CustomerService'],
    },
    {
      label: 'Automations',
      icon: <Zap size={20} />,
      allowedRoles: [],
      children: [
        { label: 'Website Sales Reports', to: '/automations/website-sales-reports', icon: <BarChart size={16} /> },
        { label: 'Customer Datafeeds', to: '/automations/customer-datafeeds', icon: <Database size={16} /> },
      ],
    },
    {
      label: 'Admin',
      icon: <Shield size={20} />,
      allowedRoles: [],
      children: [
        { label: 'New Customer', to: '/admin/new-customer', icon: <UserPlus size={16} /> },
        { label: 'Customer Config', to: '/admin/customer-config', icon: <UserCog size={16} /> },
        { label: 'Vehicle Applications', to: '/vehicle-applications/vehicles', icon: <Car size={16} /> },
        {
          label: 'Sewers',
          icon: <Users size={16} />,
          children: [
            { label: 'Employee Management', to: '/admin/employee-management', icon: <UserCog size={16} /> },
            { label: 'Sewing Report', to: '/admin/sewing-report', icon: <BarChart3 size={16} /> },
          ],
        },
        {
          label: 'Product Maintenance',
          icon: <DollarSign size={16} />,
          children: [
            { label: 'Seat Cover Price Sheet', to: '/admin/price-sheet-management', icon: <DollarSign size={16} /> },
            { label: 'Dash Cover Price Sheet', to: '/admin/dash-cover-price-sheet-management', icon: <DollarSign size={16} /> },
          ],
        },
        { label: 'Shipping Methods', to: '/admin/shipping-methods', icon: <Route size={16} /> },
        { label: 'Configurator Uploads', to: '/admin/configurator-uploads', icon: <Upload size={16} /> },
        { label: 'Users', to: '/users', icon: <Users size={16} /> },
      ],
    },
    {
      label: 'Business Dashboard',
      icon: <PieChart size={20} />,
      allowedRoles: ['Reporting', 'Marketing'],
      children: [
        { label: 'Sales Reports', to: '/admin/business-dashboard', icon: <BarChart3 size={16} />, allowedRoles: ['Reporting'] },
        { label: 'Dashcovers Sales', to: '/admin/dashcovers-sales', icon: <BarChart3 size={16} />, allowedRoles: ['Reporting'] },
        { label: 'Returns & Repairs Reports', to: '/admin/returns-repairs-reports', icon: <Wrench size={16} />, allowedRoles: ['Reporting'] },
        { label: 'Sales By Vehicle', to: '/admin/sales-by-vehicle', icon: <Car size={16} />, allowedRoles: ['Reporting', 'Marketing'] },
        { label: 'Manufacturing Forecast', to: '/admin/manufacturing-forecast', icon: <Factory size={16} />, allowedRoles: ['Reporting'] },
      ],
    },
  ];

  const filteredNavSections = navSections.filter(canSeeItem);

  const filterChildren = (items: NavItem[]): NavItem[] => {
    return items
      .filter(canSeeItem)
      .map(item => ({
        ...item,
        children: item.children ? filterChildren(item.children) : undefined,
      }));
  };

  const renderNavItem = (item: NavItem, depth: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const filteredChildren = hasChildren ? filterChildren(item.children!) : [];
    const sectionKey = item.label;
    const isExpanded = expandedSections[sectionKey];
    const active = item.to ? isActive(item.to) : (filteredChildren.length > 0 && isSectionActive(filteredChildren));

    if (hasChildren && filteredChildren.length === 0) return null;

    const paddingLeft = depth === 0 ? 'pl-4' : depth === 1 ? 'pl-10' : 'pl-14';

    if (hasChildren) {
      return (
        <div key={sectionKey}>
          <button
            onClick={() => toggleSection(sectionKey)}
            className={`
              sidebar-nav-item group flex items-center w-full py-2.5 pr-3 ${paddingLeft}
              text-sm font-medium rounded-lg transition-all duration-200
              ${active
                ? 'text-indigo-600 dark:text-white bg-indigo-50 dark:bg-indigo-600/20'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
              }
            `}
          >
            <span className="sidebar-icon flex-shrink-0 w-5 h-5 flex items-center justify-center">
              {item.icon}
            </span>
            <span className="sidebar-label ml-3 whitespace-nowrap overflow-hidden">{item.label}</span>
            <span className="sidebar-label ml-auto flex-shrink-0">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          </button>
          {isExpanded && sidebarExpanded && (
            <div className="mt-0.5 space-y-0.5">
              {filteredChildren.map(child => renderNavItem(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.to}
        to={item.to!}
        className={`
          sidebar-nav-item group flex items-center py-2.5 pr-3 ${paddingLeft}
          text-sm font-medium rounded-lg transition-all duration-200
          ${active
            ? 'text-indigo-600 dark:text-white bg-indigo-50 dark:bg-indigo-600/30 border-l-2 border-indigo-500 dark:border-indigo-400'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
          }
        `}
      >
        <span className="sidebar-icon flex-shrink-0 w-5 h-5 flex items-center justify-center">
          {item.icon}
        </span>
        <span className="sidebar-label ml-3 whitespace-nowrap overflow-hidden">{item.label}</span>
      </Link>
    );
  };

  // Mobile nav item renderer
  const renderMobileNavItem = (item: NavItem, depth: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const filteredChildren = hasChildren ? filterChildren(item.children!) : [];
    const sectionKey = 'mobile_' + item.label;
    const isExpanded = expandedSections[sectionKey];
    const active = item.to ? isActive(item.to) : false;

    if (hasChildren && filteredChildren.length === 0) return null;

    const pl = depth === 0 ? 'pl-4' : depth === 1 ? 'pl-8' : 'pl-12';

    if (hasChildren) {
      return (
        <div key={sectionKey}>
          <button
            onClick={() => toggleSection(sectionKey)}
            className={`flex items-center justify-between w-full py-2.5 pr-4 ${pl} text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg`}
          >
            <div className="flex items-center gap-3">
              {item.icon}
              <span>{item.label}</span>
            </div>
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          {isExpanded && (
            <div className="mt-0.5 space-y-0.5">
              {filteredChildren.map(child => renderMobileNavItem(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.to}
        to={item.to!}
        onClick={() => setMobileMenuOpen(false)}
        className={`flex items-center gap-3 py-2.5 pr-4 ${pl} text-sm font-medium rounded-lg ${
          active ? 'text-indigo-600 dark:text-white bg-indigo-50 dark:bg-indigo-600/30' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
        }`}
      >
        {item.icon}
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Desktop Sidebar */}
      <aside
        className="sidebar hidden sm:flex flex-col fixed top-0 left-0 h-full z-40 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 overflow-hidden"
        onMouseEnter={() => setSidebarExpanded(true)}
        onMouseLeave={() => {
          setSidebarExpanded(false);
          setExpandedSections({});
        }}
      >
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2 space-y-1 sidebar-scrollbar">
          {filteredNavSections.map(section => renderNavItem(section, 0))}
        </nav>

        {/* Logout at bottom */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-2">
          <button
            onClick={handleLogout}
            className="sidebar-nav-item group flex items-center w-full py-2.5 pl-4 pr-3 text-sm font-medium rounded-lg text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all duration-200"
          >
            <span className="sidebar-icon flex-shrink-0 w-5 h-5 flex items-center justify-center">
              <LogOut size={20} />
            </span>
            <span className="sidebar-label ml-3 whitespace-nowrap">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Wrapper */}
      <div className="flex-1 sm:ml-16 min-h-screen flex flex-col">
        {/* Top Header Bar */}
        <header className="sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-14 flex items-center justify-between relative px-4 sm:px-6">
          {/* Left side: mobile menu button + logo on mobile */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            {/* Logo on mobile - left aligned */}
            <Link to="/" className="sm:hidden">
              <img
                src="/logo-white-new.png"
                alt="CalTrend"
                className="h-6 hidden dark:block"
              />
              <img
                src="/logo-color.png"
                alt="CalTrend"
                className="h-6 block dark:hidden"
              />
            </Link>
          </div>

          {/* Centered logo - desktop only */}
          <div className="hidden sm:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <Link to="/">
              <img
                src="/logo-white-new.png"
                alt="CalTrend"
                className="h-7 hidden dark:block"
              />
              <img
                src="/logo-color.png"
                alt="CalTrend"
                className="h-7 block dark:hidden"
              />
            </Link>
          </div>

          {/* Right side items */}
          <div className="flex items-center space-x-3">
            <ThemeToggle />
            <MessageIcon />
            <div className="hidden sm:flex items-center space-x-3">
              <StatusSelector
                currentStatus={currentStatus}
                onStatusChange={handleStatusChange}
                disabled={updateStatusMutation.isPending}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                {user?.UserName}
              </span>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <>
            <div
              className="sm:hidden fixed inset-0 z-40 bg-black/50"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="sm:hidden fixed inset-y-0 left-0 z-50 w-72 bg-gray-50 dark:bg-gray-900 flex flex-col overflow-y-auto">
              {/* Mobile Header */}
              <div className="flex items-center justify-between h-14 px-4 border-b border-gray-200 dark:border-gray-700">
                <img src="/logo-white-new.png" alt="CalTrend" className="h-7" />
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-1 rounded-md text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Mobile User Info */}
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-300">{user?.UserName}</span>
                  <StatusSelector
                    currentStatus={currentStatus}
                    onStatusChange={handleStatusChange}
                    disabled={updateStatusMutation.isPending}
                  />
                </div>
              </div>

              {/* Mobile Navigation */}
              <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
                {filteredNavSections.map(section => renderMobileNavItem(section, 0))}
              </nav>

              {/* Mobile Logout */}
              <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-2">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="flex items-center gap-3 w-full py-2.5 pl-4 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg"
                >
                  <LogOut size={20} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </>
        )}

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
